import { Flex } from "antd";
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    AssignmentResponseType,
    Point,
    TaskAssignment,
    TaskStatus,
} from "tm-shared/types";
import { isTaskAssignment } from "tm-shared/utils";

import { ASSIGNMENT_VOCABULARY, ROUTES } from "@/constants";
import { AssignmentTaskInfo } from "@/domains/assignment";
import { usePointDistance } from "@/domains/point";
import { SVScanButton } from "@/features/SupervisorScan";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { TaskFinishButton } from "@/pages/WorkPlan/Tasks/components/TaskFinishButton";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import {
    clearTaskPayload,
    mutateAssignmentsEntityPhotos,
    patchAssignmentsEntity,
} from "@/store/slices/progress";
import { setTaskCompleted } from "@/store/slices/work";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { Bool } from "./components/Bool";
import { Photo } from "./components/Photo";
import { PhotoText } from "./components/PhotoText";
import { Text } from "./components/Text";
import { StyledBlock, StyledContainer } from "./Styled";

interface AssignmentsTaskParams {
    task: TaskAssignment;
    point: Point;
}

export const AssignmentTaskPage: FC<AssignmentsTaskParams> = ({
    task,
    point,
}) => {
    const pointId = point.id;
    const taskId = task.id;
    const assignmentType = task.context.additionalData.assignmentType;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { firstName, lastName } = useAppSelector((s) => s.user);

    const currentPayload = useAppSelector((s) => s.progress.customTask[taskId]);

    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, task.name],
    );

    const isFinishButtonVisible = useMemo(() => {
        if (task.context.responseType === AssignmentResponseType.Bool) {
            return currentPayload?.status !== TaskStatus.Incomplete;
        }

        return Boolean(
            currentPayload?.comment || currentPayload?.photos?.length,
        );
    }, [
        currentPayload?.comment,
        currentPayload?.photos?.length,
        currentPayload?.status,
        task.context.responseType,
    ]);

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const photoIds = currentPayload?.photos ?? [];
        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto,
                payload: currentPayload ?? {
                    status: TaskStatus.Completed,
                    comment: "",
                    photos: [],
                },
            },
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,
        ];

        if (photoIds.length > 0) {
            commands.push(
                makeCommand({
                    type: "updatePhotoStorage",
                    title: "Синхронізація сховища",
                    params: photoIds,
                    options: {
                        stopOnError: false,
                    },
                }),
            );
        }

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: {
                    pointId,
                    taskId,
                },
                commands,
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ targetId: pointId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });
    });

    const onChangeComment = useCallback(
        (evt: ChangeEvent<HTMLTextAreaElement>) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { comment: evt.target.value },
                }),
            );
        },
        [dispatch, taskId],
    );

    const onChangeStatus = useCallback(
        (newStatus: TaskStatus) => {
            dispatch(
                patchAssignmentsEntity({
                    taskId,
                    patch: { status: newStatus },
                }),
            );
        },
        [dispatch, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            dispatch(
                mutateAssignmentsEntityPhotos({
                    taskId,
                    option: "add",
                    photo,
                }),
            );
        },
        [dispatch, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            dispatch(
                mutateAssignmentsEntityPhotos({
                    taskId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [dispatch, taskId],
    );

    const renderContent = useMemo(() => {
        if (!task || !isTaskAssignment(task)) return null;

        const commonProps = {
            pointId,
            taskId,
            payload: currentPayload,
            captions,
            assignmentType,
        };

        switch (task.context.responseType) {
            case AssignmentResponseType.Bool:
                return (
                    <Bool {...commonProps} onChangeStatus={onChangeStatus} />
                );

            case AssignmentResponseType.Text:
                return (
                    <Text {...commonProps} onChangeComment={onChangeComment} />
                );

            case AssignmentResponseType.Photo:
                return (
                    <Photo
                        {...commonProps}
                        title={task.name}
                        onUploadPhoto={onUploadPhoto}
                        onDeletePhoto={onDeletePhoto}
                    />
                );

            case AssignmentResponseType.PhotoText:
                return (
                    <PhotoText
                        {...commonProps}
                        title={task.name}
                        onChangeComment={onChangeComment}
                        onUploadPhoto={onUploadPhoto}
                        onDeletePhoto={onDeletePhoto}
                    />
                );

            default:
                return <div>Невідомий тип доручення</div>;
        }
    }, [
        task,
        pointId,
        taskId,
        currentPayload,
        captions,
        assignmentType,
        onChangeStatus,
        onChangeComment,
        onUploadPhoto,
        onDeletePhoto,
    ]);

    const assignmentLabel = ASSIGNMENT_VOCABULARY[assignmentType]?.label;

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <SVScanButton pointId={pointId} taskId={taskId} />
                }
            />

            <MainContainer>
                <StyledContainer>
                    <Flex vertical gap="16px">
                        <StyledBlock>
                            <AssignmentTaskInfo
                                assignmentType={assignmentLabel}
                                description={task.context.description}
                                images={task.context.files}
                            />
                        </StyledBlock>

                        {renderContent}
                    </Flex>

                    <TaskFinishButton
                        onClick={handleComplete}
                        visible={isFinishButtonVisible}
                        loading={completeLoading}
                        padding="0px"
                    />
                </StyledContainer>
            </MainContainer>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};

export default AssignmentTaskPage;
